import { useMemo, useRef, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  DataTable,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  SectionCard,
  Skeleton,
  RequiredMark,
  type DataTableColumn,
} from '@/components/ui';
import { Pencil, Trash2, Copy as CopyIcon, Upload } from 'lucide-react';
import type { ReactNode } from 'react';
import { friendlyMessage } from '@/lib/errors';
import { usePopupSlotMutations, usePopupSlotsQuery } from '../hooks/usePopupConfig';
import type { PopupSlotView } from '../types/popupDetail';
import { TimeSelect5 } from './TimeSelect5';

interface SlotDraft {
  slotDate: string;
  timeStart: string;
  timeEnd: string;
  maxParticipants: string;
}

const EMPTY_DRAFT: SlotDraft = {
  slotDate: '',
  timeStart: '',
  timeEnd: '',
  maxParticipants: '',
};

export interface PopupSlotSectionProps {
  popupId: string;
}

/** 슬롯 추가 그리드 셀: 라벨을 인풋 위에 쌓아 인풋이 셀 전체 폭을 갖게 한다(date 인풋 클리핑 방지). */
const SlotField = ({
  label,
  required = false,
  children,
}: {
  label: string;
  // QA-56: 슬롯 추가/수정의 날짜·시간·정원은 저장 검증이 강제하는 필수 필드.
  required?: boolean;
  children: ReactNode;
}) => (
  <label className="flex flex-col gap-1">
    <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
      {label}
      {required && <RequiredMark />}
    </span>
    {children}
  </label>
);

const toEditDraft = (slot: PopupSlotView): SlotDraft => ({
  slotDate: slot.slotDate,
  // 시간은 "HH:mm" 으로 정규화(서버가 "HH:mm:ss" 로 보낼 수 있음).
  timeStart: slot.timeStart.slice(0, 5),
  timeEnd: slot.timeEnd.slice(0, 5),
  // QA-R6 N: 무제한(null/0 이하)은 빈 입력으로 표시(0 을 그대로 보여주면 정원 0명으로 오인).
  maxParticipants:
    slot.maxParticipants != null && slot.maxParticipants > 0 ? String(slot.maxParticipants) : '',
});

// QA-R6 N: 정원 미입력 = 무제한(0). BE 가 maxParticipants≤0 을 무제한으로 해석(Lead 확정).
// 빈값/0/음수 = 무제한(0), 양의 정수 = 그 값, 소수·문자 = null(무효 입력 → 저장 차단).
const draftToNumber = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (trimmed === '') return 0; // 미입력 = 무제한.
  const n = Number(trimmed);
  if (!Number.isInteger(n)) return null;
  return n > 0 ? n : 0; // 0·음수 = 무제한.
};

export const PopupSlotSection = ({ popupId }: PopupSlotSectionProps) => {
  const { t } = useTranslation('common');
  const [filterDate, setFilterDate] = useState('');
  const query = usePopupSlotsQuery(popupId, filterDate || undefined);
  const { create, update, remove, copy, bulkUpload } = usePopupSlotMutations(popupId);

  const [draft, setDraft] = useState<SlotDraft>(EMPTY_DRAFT);
  const [error, setError] = useState<string | null>(null);

  // ID36: 슬롯 사후 수정 — 편집 대상 슬롯 + 모달 draft.
  const [editing, setEditing] = useState<PopupSlotView | null>(null);
  const [editDraft, setEditDraft] = useState<SlotDraft>(EMPTY_DRAFT);
  const [editError, setEditError] = useState<string | null>(null);

  // QA-181: 회차 복사 모달 + 엑셀 업로드.
  const [copyOpen, setCopyOpen] = useState(false);
  const [copySource, setCopySource] = useState('');
  const [copyTargets, setCopyTargets] = useState('');
  const [copyError, setCopyError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const slots = useMemo(() => query.data ?? [], [query.data]);

  // QA-181: 종료시간 optional — 시작 시/분만으로 슬롯 추가 가능.
  // QA-R6 N: 정원은 미입력 허용(= 무제한). 더 이상 정원 입력을 강제하지 않는다.
  const canSubmit = draft.slotDate !== '' && draft.timeStart !== '';

  const onAdd = () => {
    setError(null);
    if (!canSubmit) {
      setError(t('cms.popups.slots.validation'));
      return;
    }
    // QA-R6 N: 빈값/0/음수 = 무제한(0). 소수·문자만 무효(null)로 차단.
    const max = draftToNumber(draft.maxParticipants);
    if (max == null) {
      setError(t('cms.popups.slots.validation'));
      return;
    }
    create.mutate(
      {
        slotDate: draft.slotDate,
        timeStart: draft.timeStart,
        // QA-181: 종료시간 미입력 시 생략(BE 가 시작시각으로 설정).
        ...(draft.timeEnd ? { timeEnd: draft.timeEnd } : {}),
        maxParticipants: max,
      },
      {
        onSuccess: () => setDraft(EMPTY_DRAFT),
        onError: (e) => setError(friendlyMessage(e, t('cms.popups.slots.saveFailed', { defaultValue: '슬롯 저장에 실패했습니다.' }))),
      },
    );
  };

  // QA-181: 회차 복사 — sourceDate 회차구성을 targetDates(줄바꿈/콤마 구분)에 적용.
  const onCopy = () => {
    setCopyError(null);
    const targets = copyTargets
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s));
    if (!/^\d{4}-\d{2}-\d{2}$/.test(copySource) || targets.length === 0) {
      setCopyError(t('cms.popups.slots.copyValidation', { defaultValue: '원본 일자와 대상 일자를 올바르게 입력해 주세요.' }));
      return;
    }
    copy.mutate(
      { sourceDate: copySource, targetDates: targets },
      {
        onSuccess: () => {
          setCopyOpen(false);
          setCopySource('');
          setCopyTargets('');
        },
        onError: (e) =>
          setCopyError(friendlyMessage(e, t('cms.popups.slots.copyFailed', { defaultValue: '회차 복사에 실패했습니다.' }))),
      },
    );
  };

  // QA-181: 엑셀 일괄 업로드.
  const onBulkFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(null);
    bulkUpload.mutate(file, {
      onError: (err) =>
        setError(friendlyMessage(err, t('cms.popups.slots.bulkFailed', { defaultValue: '엑셀 일괄 업로드에 실패했습니다.' }))),
    });
  };

  const openEdit = (slot: PopupSlotView) => {
    setEditError(null);
    setEditing(slot);
    setEditDraft(toEditDraft(slot));
  };

  const closeEdit = () => {
    setEditing(null);
    setEditError(null);
  };

  // QA-R6 N: 정원 미입력 허용(= 무제한). 날짜/시작/종료만 필수.
  const editCanSubmit =
    editDraft.slotDate !== '' &&
    editDraft.timeStart !== '' &&
    editDraft.timeEnd !== '';

  const onEditSave = () => {
    if (!editing) return;
    setEditError(null);
    // QA-R6 N: 빈값/0/음수 = 무제한(0). 소수·문자만 무효(null)로 차단.
    const max = draftToNumber(editDraft.maxParticipants);
    if (!editCanSubmit || max == null) {
      setEditError(t('cms.popups.slots.validation'));
      return;
    }
    update.mutate(
      {
        slotId: editing.id,
        body: {
          slotDate: editDraft.slotDate,
          timeStart: editDraft.timeStart,
          timeEnd: editDraft.timeEnd,
          maxParticipants: max,
        },
      },
      {
        onSuccess: () => closeEdit(),
        // BE 400(5분 단위/현재예약 미만 정원), 404(미존재) 메시지를 그대로 노출.
        onError: (e) =>
          setEditError(
            friendlyMessage(e, t('cms.popups.slots.updateFailed', { defaultValue: '슬롯 수정에 실패했습니다.' })),
          ),
      },
    );
  };

  // ID36: 공개 응답과 달리 admin 슬롯 조회는 정원/현재예약을 항상 채운다.
  // 다만 타입상 nullable 이므로 null 안전하게 표시한다.
  const capacityCell = (row: PopupSlotView): string => {
    const current = row.currentReservations ?? 0;
    const max = row.maxParticipants;
    // catalog v1.0.49 — 정원 0(미입력) 슬롯은 "슬롯 제한 없음, 팝업 일별 총 정원까지"(종전 표기 '무제한'은 실제와 달랐다).
    return max != null && max > 0
      ? `${current} / ${max}`
      : `${current} / ${t('cms.popups.slots.dailyCapacityBasis', { defaultValue: '일별 총 정원 기준' })}`;
  };

  const columns: ReadonlyArray<DataTableColumn<PopupSlotView>> = [
    {
      key: 'slotDate',
      header: t('cms.popups.slots.cols.date'),
      width: 130,
      cell: (row) => (
        <span className="text-sm tabular-nums" style={{ color: 'var(--content-strong)' }}>
          {row.slotDate}
        </span>
      ),
    },
    {
      key: 'time',
      header: t('cms.popups.slots.cols.time'),
      cell: (row) => (
        <span className="text-sm tabular-nums" style={{ color: 'var(--content-default)' }}>
          {row.timeStart.slice(0, 5)} ~ {row.timeEnd.slice(0, 5)}
        </span>
      ),
    },
    {
      key: 'capacity',
      header: t('cms.popups.slots.cols.capacity'),
      align: 'right',
      width: 120,
      cell: (row) => (
        <span className="text-sm tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {capacityCell(row)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: 96,
      cell: (row) => (
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            aria-label={t('cms.popups.slots.edit', { defaultValue: '슬롯 수정' })}
            onClick={() => openEdit(row)}
          >
            <Pencil size={14} aria-hidden="true" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={remove.isPending}
            aria-label={t('cms.popups.slots.remove')}
            onClick={() => remove.mutate(row.id)}
          >
            <Trash2 size={14} aria-hidden="true" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <SectionCard title={t('cms.popups.slots.title')}>
      {/* 슬롯 추가 행. FormSection compact 는 140px 라벨을 인풋 옆에 붙여, 5열 그리드의 좁은
       * 셀에서 네이티브 date 인풋이 잘려 '0222-02-' 처럼 보였다. 라벨을 인풋 위로 쌓아
       * 인풋이 셀 전체 폭을 갖도록 해 yyyy-MM-dd 가 온전히 렌더된다.
       * ID36: 시간 셀렉트는 5분 단위(BE minute%5==0 검증). */}
      <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-5">
        <SlotField label={t('cms.popups.slots.cols.date')} required>
          <Input
            type="date"
            value={draft.slotDate}
            onChange={(e) => setDraft((p) => ({ ...p, slotDate: e.target.value }))}
          />
        </SlotField>
        <SlotField label={t('cms.popups.slots.timeStart')} required>
          <TimeSelect5
            value={draft.timeStart}
            onChange={(v) => setDraft((p) => ({ ...p, timeStart: v }))}
            ariaLabel={t('cms.popups.slots.timeStart')}
          />
        </SlotField>
        {/* QA-181: 종료시간 optional — 미입력 시 시작시각으로 운영(시작 시/분만 세팅). */}
        <SlotField label={t('cms.popups.slots.timeEndOptional', { defaultValue: '종료 시간 (선택)' })}>
          <TimeSelect5
            value={draft.timeEnd}
            onChange={(v) => setDraft((p) => ({ ...p, timeEnd: v }))}
            ariaLabel={t('cms.popups.slots.timeEndOptional', { defaultValue: '종료 시간 (선택)' })}
          />
        </SlotField>
        {/* QA-R6 N: 정원 미입력 = 무제한(0 기본값 footgun 제거). required 해제 + 무제한 placeholder. */}
        <SlotField label={t('cms.popups.slots.maxParticipants')}>
          <Input
            inputMode="numeric"
            min={0}
            type="number"
            placeholder={t('cms.popups.slots.unlimitedPlaceholder', { defaultValue: '비우면 무제한' })}
            value={draft.maxParticipants}
            onChange={(e) => setDraft((p) => ({ ...p, maxParticipants: e.target.value }))}
          />
        </SlotField>
        <div className="flex items-end">
          <Button size="sm" className="w-full" disabled={create.isPending || !canSubmit} onClick={onAdd}>
            {t('cms.popups.slots.add')}
          </Button>
        </div>
      </div>
      {/* catalog v1.0.49 — 정원 0(미입력) = 팝업 일별 총 정원까지. 일별 총 정원도 없으면 예약 불가(매진). */}
      <p className="mt-2 text-[12px]" style={{ color: 'var(--content-muted)' }} data-testid="slot-capacity-help">
        {t('cms.popups.slots.capacityHelp', {
          defaultValue: "슬롯 정원을 비우거나 0으로 두면 팝업 '일별 총 정원'까지 예약을 받습니다. 일별 총 정원이 없으면 예약할 수 없습니다.",
        })}
      </p>

      {/* QA-181: 회차 복사 + 엑셀 일괄 업로드 — 다일정 운영 효율화. ★업로드 파일은 multipart 로
          서버(POI)가 파싱한다(BO 클라 파싱 없음). 상품 import 동형. */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => { setCopyError(null); setCopyOpen(true); }}>
          <CopyIcon size={14} className="mr-1" aria-hidden="true" />
          {t('cms.popups.slots.copyAction', { defaultValue: '회차 복사' })}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={bulkUpload.isPending}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload size={14} className="mr-1" aria-hidden="true" />
          {bulkUpload.isPending
            ? t('cms.popups.slots.bulkUploading', { defaultValue: '업로드 중…' })
            : t('cms.popups.slots.bulkAction', { defaultValue: '엑셀 일괄 업로드' })}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          className="hidden"
          onChange={onBulkFile}
          aria-hidden="true"
        />
        <span className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
          {t('cms.popups.slots.bulkHint', {
            defaultValue: '열: 일자 / 시작시간 / 종료시간(선택) / 정원',
          })}
        </span>
      </div>

      {error ? (
        <p className="mb-2 text-xs" style={{ color: 'var(--status-danger-fg)' }}>
          {error}
        </p>
      ) : null}

      <div className="mb-2 flex items-end gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
            {t('cms.popups.slots.filterDate')}
          </span>
          <Input
            type="date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            className="w-[200px]"
          />
        </label>
        {filterDate ? (
          <Button variant="ghost" size="sm" onClick={() => setFilterDate('')}>
            {t('cms.popups.slots.filterClear')}
          </Button>
        ) : null}
      </div>

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-[40px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          className="rounded-md p-3 text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          {t('cms.popups.slots.loadFailed')}
        </div>
      ) : (
        <DataTable
          columns={[...columns]}
          rows={[...slots]}
          rowKey={(row) => row.id}
          emptyState={t('cms.popups.slots.empty')}
          stickyHeader
        />
      )}

      {/* ID36: 슬롯 사후 수정 모달. 생성 폼과 동일한 필드(날짜/시작/종료/정원). */}
      <Dialog open={editing !== null} onOpenChange={(open) => { if (!open) closeEdit(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('cms.popups.slots.editTitle', { defaultValue: '슬롯 수정' })}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <SlotField label={t('cms.popups.slots.cols.date')} required>
              <Input
                type="date"
                value={editDraft.slotDate}
                onChange={(e) => setEditDraft((p) => ({ ...p, slotDate: e.target.value }))}
              />
            </SlotField>
            <div className="grid grid-cols-2 gap-3">
              <SlotField label={t('cms.popups.slots.timeStart')} required>
                <TimeSelect5
                  value={editDraft.timeStart}
                  onChange={(v) => setEditDraft((p) => ({ ...p, timeStart: v }))}
                  ariaLabel={t('cms.popups.slots.timeStart')}
                />
              </SlotField>
              <SlotField label={t('cms.popups.slots.timeEnd')} required>
                <TimeSelect5
                  value={editDraft.timeEnd}
                  onChange={(v) => setEditDraft((p) => ({ ...p, timeEnd: v }))}
                  ariaLabel={t('cms.popups.slots.timeEnd')}
                />
              </SlotField>
            </div>
            {/* QA-R6 N: 정원 미입력 = 무제한. required 해제 + 무제한 placeholder. */}
            <SlotField label={t('cms.popups.slots.maxParticipants')}>
              <Input
                inputMode="numeric"
                min={0}
                type="number"
                placeholder={t('cms.popups.slots.unlimitedPlaceholder', { defaultValue: '비우면 무제한' })}
                value={editDraft.maxParticipants}
                onChange={(e) => setEditDraft((p) => ({ ...p, maxParticipants: e.target.value }))}
              />
            </SlotField>
            <p className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
              {t('cms.popups.slots.capacityHelp', {
                defaultValue: "슬롯 정원을 비우거나 0으로 두면 팝업 '일별 총 정원'까지 예약을 받습니다. 일별 총 정원이 없으면 예약할 수 없습니다.",
              })}
            </p>
            {editing != null ? (
              <p className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
                {t('cms.popups.slots.currentReservationsHint', {
                  defaultValue: '현재 예약 {{count}}명. 정원을 현재 예약 인원보다 적게 낮출 수 없습니다.',
                  count: editing.currentReservations ?? 0,
                })}
              </p>
            ) : null}
            {editError ? (
              <p className="text-xs" style={{ color: 'var(--status-danger-fg)' }}>
                {editError}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('cms.cancel', { defaultValue: '취소' })}</Button>
            </DialogClose>
            <Button size="sm" disabled={update.isPending || !editCanSubmit} onClick={onEditSave}>
              {t('cms.save', { defaultValue: '저장' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QA-181: 회차 복사 모달 — 한 일자의 회차 구성을 다른 일자들에 동일 적용. */}
      <Dialog open={copyOpen} onOpenChange={(open) => { if (!open) setCopyOpen(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('cms.popups.slots.copyTitle', { defaultValue: '회차 복사' })}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <SlotField label={t('cms.popups.slots.copySource', { defaultValue: '원본 일자' })} required>
              <Input
                type="date"
                value={copySource}
                onChange={(e) => setCopySource(e.target.value)}
              />
            </SlotField>
            <label className="flex flex-col gap-1">
              <span className="text-[12px] font-medium" style={{ color: 'var(--content-muted)' }}>
                {t('cms.popups.slots.copyTargets', { defaultValue: '대상 일자 (여러 개는 줄바꿈/쉼표 구분)' })}
                <RequiredMark />
              </span>
              <textarea
                value={copyTargets}
                onChange={(e) => setCopyTargets(e.target.value)}
                rows={3}
                placeholder={'2025-12-14\n2025-12-15'}
                className="w-full rounded-md border px-3 py-2 text-sm bg-[color:var(--surface-0)] text-[color:var(--content-strong)] focus-visible:outline-none focus-visible:ring-2"
                style={{ borderColor: 'var(--content-muted)' }}
              />
            </label>
            <p className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
              {t('cms.popups.slots.copyHint', {
                defaultValue: '원본 일자의 모든 회차(시간·정원)가 대상 일자에 동일하게 생성됩니다.',
              })}
            </p>
            {copyError ? (
              <p className="text-xs" style={{ color: 'var(--status-danger-fg)' }}>
                {copyError}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('cms.cancel', { defaultValue: '취소' })}</Button>
            </DialogClose>
            <Button size="sm" disabled={copy.isPending} onClick={onCopy}>
              {copy.isPending
                ? t('cms.popups.slots.copying', { defaultValue: '복사 중…' })
                : t('cms.popups.slots.copyConfirm', { defaultValue: '복사' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
};
